// frontend/src/components/Dashboard/BoletinManager.js
// Boletín de noticias por correo (panel admin > Notificaciones). Historial
// de envíos + editor con dos modos:
//  - Visual: el correo real (vista previa del backend) dentro de un iframe,
//    donde solo el bloque #contenido-correo es editable. El header con el
//    logo y el pie con el aviso de baja quedan fuera y no se pueden borrar.
//  - HTML / CSS: edición del código del contenido y de CSS adicional, con
//    vista previa en vivo.
// Ver backend/src/routes/boletines.js.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Newspaper, Plus, Send, Eye, Code, Copy, ArrowLeft, RotateCcw, Loader2,
  Bold, Italic, Underline, Heading2, Heading3, Pilcrow, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link, Image, MousePointerClick, Minus,
  RemoveFormatting, Undo2, Redo2, CheckCircle2, AlertTriangle, Clock
} from 'lucide-react';
import MotivosBajaChart from './MotivosBajaChart';
import './BoletinManager.css';

const CLAVE_BORRADOR = 'psytest_boletin_borrador';

const PLANTILLA_INICIAL = {
  asunto: '',
  contenido: `<h2>Novedades de PsyTest</h2>
<p>Hola,</p>
<p>Te compartimos las novedades más recientes de la plataforma.</p>
<h3>¿Qué hay de nuevo?</h3>
<ul>
  <li>Primera novedad</li>
  <li>Segunda novedad</li>
</ul>
<p style="text-align: center;"><a href="https://psytest.com.mx" class="button">Conoce más</a></p>
<p>Saludos,<br>El equipo de PsyTest</p>`,
  css: ''
};

const leerBorrador = () => {
  try {
    const guardado = JSON.parse(localStorage.getItem(CLAVE_BORRADOR));
    return guardado && typeof guardado.contenido === 'string' ? guardado : null;
  } catch { return null; }
};

const guardarBorrador = (borrador) => {
  try { localStorage.setItem(CLAVE_BORRADOR, JSON.stringify(borrador)); } catch { /* sin localStorage no se conserva el borrador */ }
};

const borrarBorrador = () => {
  try { localStorage.removeItem(CLAVE_BORRADOR); } catch { /* nada que borrar */ }
};

// Si el backend desplegado no tiene /api/boletines, Express responde su
// página 404 en HTML; sin esto el error sería un críptico "not valid JSON".
// Incluye el inicio de la respuesta para poder diagnosticar quién respondió
// (Express, el proxy de desarrollo, nginx...).
const leerJson = async (response) => {
  const texto = await response.text();
  try {
    return JSON.parse(texto);
  } catch {
    const extracto = texto.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
    throw new Error(`El servidor respondió ${response.status} sin datos válidos (${response.url.replace(window.location.origin, '')}). Respuesta: "${extracto || 'vacía'}"`);
  }
};

const ESTADOS = {
  enviando: { texto: 'Enviando', icono: Clock, clase: 'enviando' },
  enviado: { texto: 'Enviado', icono: CheckCircle2, clase: 'enviado' },
  con_errores: { texto: 'Con errores', icono: AlertTriangle, clase: 'con-errores' }
};

const BoletinManager = ({ showModal, modoOscuro }) => {
  const [modo, setModo] = useState('lista'); // 'lista' | 'editor'
  const [boletines, setBoletines] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [suscriptores, setSuscriptores] = useState(null);

  const [asunto, setAsunto] = useState('');
  const [contenido, setContenido] = useState('');
  const [css, setCss] = useState('');
  const [vista, setVista] = useState('visual'); // 'visual' | 'codigo'
  const [srcDoc, setSrcDoc] = useState('');
  const [errorVistaPrevia, setErrorVistaPrevia] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);

  const iframeRef = useRef(null);
  // showModal cambia en cada render del Dashboard; la ref evita reiniciar el polling
  const showModalRef = useRef(showModal);
  showModalRef.current = showModal;
  // Valores actuales para efectos que no deben re-dispararse al escribir
  const contenidoRef = useRef(contenido);
  const cssRef = useRef(css);
  contenidoRef.current = contenido;
  cssRef.current = css;

  const cargarLista = useCallback(async () => {
    try {
      const [listaRes, conteoRes] = await Promise.all([
        fetch('/api/boletines', { credentials: 'include' }),
        fetch('/api/boletines/suscriptores/conteo', { credentials: 'include' })
      ]);
      const lista = await leerJson(listaRes);
      const conteo = await leerJson(conteoRes);
      if (!listaRes.ok) throw new Error(lista.error || 'No se pudo cargar el historial de boletines');
      setBoletines(Array.isArray(lista) ? lista : []);
      setSuscriptores(conteoRes.ok ? conteo.total : null);
    } catch (error) {
      showModalRef.current({ type: 'error', title: 'Error al cargar boletines', message: error.message });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  // Mientras haya un envío en curso, refresca el avance cada 5 s
  const hayEnvioEnCurso = boletines.some(b => b.estado === 'enviando');
  useEffect(() => {
    if (!hayEnvioEnCurso || modo !== 'lista') return undefined;
    const id = setInterval(cargarLista, 5000);
    return () => clearInterval(id);
  }, [hayEnvioEnCurso, modo, cargarLista]);

  const cargarVistaPrevia = async (html, estilos) => {
    try {
      const response = await fetch('/api/boletines/vista-previa', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contenido_html: html, css: estilos })
      });
      const data = await leerJson(response);
      if (!response.ok) throw new Error(data.error || 'No se pudo generar la vista previa');
      setSrcDoc(data.html);
      setErrorVistaPrevia(null);
    } catch (error) {
      setErrorVistaPrevia(error.message);
    }
  };

  // Modo visual: el iframe se carga una vez al entrar (no en cada tecla,
  // para no perder el cursor).
  useEffect(() => {
    if (modo === 'editor' && vista === 'visual') cargarVistaPrevia(contenidoRef.current, cssRef.current);
  }, [modo, vista]);

  // Modo código: vista previa en vivo con un pequeño retraso
  useEffect(() => {
    if (modo !== 'editor' || vista !== 'codigo') return undefined;
    const id = setTimeout(() => cargarVistaPrevia(contenido, css), 400);
    return () => clearTimeout(id);
  }, [modo, vista, contenido, css]);

  // Borrador local: si se cierra la pestaña no se pierde lo escrito
  useEffect(() => {
    if (modo === 'editor') guardarBorrador({ asunto, contenido, css });
  }, [modo, asunto, contenido, css]);

  const bloqueEditable = () => iframeRef.current?.contentDocument?.getElementById('contenido-correo');

  const sincronizarDesdeIframe = () => {
    const el = bloqueEditable();
    if (el) setContenido(el.innerHTML);
  };

  const handleIframeLoad = () => {
    if (vista !== 'visual') return;
    const doc = iframeRef.current?.contentDocument;
    const el = bloqueEditable();
    if (!doc || !el) return;
    el.contentEditable = 'true';
    el.spellcheck = true;
    el.style.outline = 'none';
    el.style.minHeight = '200px';
    doc.execCommand('defaultParagraphSeparator', false, 'p');
    el.addEventListener('input', sincronizarDesdeIframe);
  };

  const comando = (nombre, valor = null) => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !bloqueEditable()) return;
    win.focus();
    win.document.execCommand(nombre, false, valor);
    sincronizarDesdeIframe();
  };

  const insertarEnlace = () => {
    const url = window.prompt('URL del enlace (https://...)');
    if (url) comando('createLink', url);
  };

  const insertarImagen = () => {
    const url = window.prompt('URL pública de la imagen (https://...)');
    if (url) comando('insertHTML', `<img src="${url}" alt="" style="max-width: 100%; height: auto;">`);
  };

  const insertarBoton = () => {
    const texto = window.prompt('Texto del botón', 'Conoce más');
    if (!texto) return;
    const url = window.prompt('URL a la que lleva el botón (https://...)');
    if (!url) return;
    comando('insertHTML', `<p style="text-align: center;"><a href="${url}" class="button">${texto}</a></p><p><br></p>`);
  };

  const abrirEditor = (base) => {
    const inicial = base || leerBorrador() || PLANTILLA_INICIAL;
    setAsunto(inicial.asunto || '');
    setContenido(inicial.contenido);
    setCss(inicial.css || '');
    setVista('visual');
    setSrcDoc('');
    setModo('editor');
  };

  const empezarDeCero = () => {
    showModal({
      type: 'warning',
      title: 'Empezar de cero',
      message: 'Se descartará el contenido actual del editor y se cargará la plantilla inicial.',
      showCancelButton: true,
      confirmText: 'Descartar',
      onConfirm: () => {
        setAsunto(PLANTILLA_INICIAL.asunto);
        setContenido(PLANTILLA_INICIAL.contenido);
        setCss(PLANTILLA_INICIAL.css);
        if (vista === 'visual') cargarVistaPrevia(PLANTILLA_INICIAL.contenido, PLANTILLA_INICIAL.css);
      }
    });
  };

  const validar = () => {
    if (!asunto.trim()) return 'Escribe el asunto del correo.';
    const texto = contenido.replace(/<[^>]*>/g, '').trim();
    if (!texto && !/<img/i.test(contenido)) return 'El contenido del correo está vacío.';
    return null;
  };

  const cuerpoPeticion = () => JSON.stringify({ asunto: asunto.trim(), contenido_html: contenido, css });

  const enviarPrueba = async () => {
    const error = validar();
    if (error) return showModal({ type: 'warning', title: 'Falta información', message: error });
    setEnviandoPrueba(true);
    try {
      const response = await fetch('/api/boletines/prueba', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: cuerpoPeticion()
      });
      const data = await leerJson(response);
      if (!response.ok) throw new Error(data.error || 'No se pudo enviar la prueba');
      showModal({ type: 'success', title: 'Prueba enviada', message: `Revisa la bandeja de ${data.email}.` });
    } catch (err) {
      showModal({ type: 'error', title: 'Error al enviar la prueba', message: err.message });
    } finally {
      setEnviandoPrueba(false);
    }
  };

  const enviarBoletin = async () => {
    setEnviando(true);
    try {
      const response = await fetch('/api/boletines', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: cuerpoPeticion()
      });
      const data = await leerJson(response);
      if (!response.ok) throw new Error(data.error || 'No se pudo enviar el boletín');
      borrarBorrador();
      setModo('lista');
      cargarLista();
      showModal({
        type: 'success',
        title: 'Boletín en camino',
        message: `Se está enviando a ${data.total_destinatarios} suscriptor(es). Puedes ver el avance en el historial.`
      });
    } catch (err) {
      showModal({ type: 'error', title: 'Error al enviar el boletín', message: err.message });
    } finally {
      setEnviando(false);
    }
  };

  const confirmarEnvio = () => {
    const error = validar();
    if (error) return showModal({ type: 'warning', title: 'Falta información', message: error });
    if (!suscriptores) {
      return showModal({ type: 'warning', title: 'Sin suscriptores', message: 'Todavía no hay empresas suscritas al boletín.' });
    }
    showModal({
      type: 'warning',
      title: 'Enviar boletín',
      message: `Se enviará "${asunto.trim()}" a ${suscriptores} suscriptor(es). Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmText: 'Enviar ahora',
      onConfirm: enviarBoletin
    });
  };

  const BOTONES = [
    [
      { icono: Bold, titulo: 'Negrita', accion: () => comando('bold') },
      { icono: Italic, titulo: 'Cursiva', accion: () => comando('italic') },
      { icono: Underline, titulo: 'Subrayado', accion: () => comando('underline') }
    ],
    [
      { icono: Heading2, titulo: 'Título', accion: () => comando('formatBlock', '<h2>') },
      { icono: Heading3, titulo: 'Subtítulo', accion: () => comando('formatBlock', '<h3>') },
      { icono: Pilcrow, titulo: 'Párrafo', accion: () => comando('formatBlock', '<p>') }
    ],
    [
      { icono: List, titulo: 'Lista con viñetas', accion: () => comando('insertUnorderedList') },
      { icono: ListOrdered, titulo: 'Lista numerada', accion: () => comando('insertOrderedList') }
    ],
    [
      { icono: AlignLeft, titulo: 'Alinear a la izquierda', accion: () => comando('justifyLeft') },
      { icono: AlignCenter, titulo: 'Centrar', accion: () => comando('justifyCenter') },
      { icono: AlignRight, titulo: 'Alinear a la derecha', accion: () => comando('justifyRight') }
    ],
    [
      { icono: Link, titulo: 'Insertar enlace', accion: insertarEnlace },
      { icono: Image, titulo: 'Insertar imagen (URL)', accion: insertarImagen },
      { icono: MousePointerClick, titulo: 'Insertar botón', accion: insertarBoton },
      { icono: Minus, titulo: 'Línea divisoria', accion: () => comando('insertHorizontalRule') }
    ],
    [
      { icono: RemoveFormatting, titulo: 'Quitar formato', accion: () => comando('removeFormat') },
      { icono: Undo2, titulo: 'Deshacer', accion: () => comando('undo') },
      { icono: Redo2, titulo: 'Rehacer', accion: () => comando('redo') }
    ]
  ];

  const iframe = errorVistaPrevia && !srcDoc ? (
    <div className="boletin-error-vista">
      <strong>No se pudo cargar el correo</strong>
      <p>{errorVistaPrevia}</p>
      <button type="button" className="btn-secondary" onClick={() => cargarVistaPrevia(contenido, css)}>
        <RotateCcw size={13} /> Reintentar
      </button>
    </div>
  ) : (
    <iframe
      ref={iframeRef}
      title={vista === 'visual' ? 'Editor del boletín' : 'Vista previa del boletín'}
      className="boletin-iframe"
      srcDoc={srcDoc}
      onLoad={handleIframeLoad}
      sandbox="allow-same-origin"
    />
  );

  return (
    <div className="dash-page-card boletin-card">
      <div className="stack-manager-header">
        <h2><Newspaper size={18} /> Boletín de noticias</h2>
        <button type="button" onClick={() => abrirEditor()} className="btn-success">
          <Plus size={14} /> Nuevo boletín
        </button>
      </div>
      <p className="text-muted boletin-sub">
        Correo masivo para los usuarios de empresa que aceptaron recibir actualizaciones de producto y marketing
        {suscriptores !== null && <> · <strong>{suscriptores}</strong> suscriptor(es) actualmente</>}.
      </p>

      {cargando ? (
        <p className="text-muted">Cargando...</p>
      ) : boletines.length === 0 ? (
        <p className="text-muted">Aún no se ha enviado ningún boletín.</p>
      ) : (
        <div className="stack-list">
          {boletines.map(b => {
            const estado = ESTADOS[b.estado] || ESTADOS.enviado;
            const IconoEstado = estado.icono;
            return (
              <div key={b.id} className="stack-row">
                <div className="stack-row-info">
                  <strong>{b.asunto}</strong>
                  <span className={`boletin-estado ${estado.clase}`}>
                    <IconoEstado size={11} /> {estado.texto} · {b.enviados}/{b.total_destinatarios} enviados
                    {b.fallidos > 0 && ` · ${b.fallidos} fallidos`}
                  </span>
                  <span className="badge-count">
                    {new Date(b.created_at).toLocaleString('es-MX')}{b.creado_por ? ` · ${b.creado_por}` : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => abrirEditor({ asunto: b.asunto, contenido: b.contenido_html, css: b.css })}
                  title="Abrir en el editor con este contenido"
                >
                  <Copy size={13} /> Usar como base
                </button>
              </div>
            );
          })}
        </div>
      )}

      <MotivosBajaChart modoOscuro={modoOscuro} recargar={suscriptores} />

      {modo === 'editor' && (
        <div className="modal-overlay">
          <div className="modal-content has-sticky-header boletin-modal">
            <div className="modal-sticky-header">
              <h2>Nuevo boletín</h2>
              <div className="modal-sticky-header-actions">
                <button type="button" onClick={enviarPrueba} className="btn-secondary" disabled={enviandoPrueba || enviando}>
                  {enviandoPrueba ? <Loader2 size={13} className="spin-icon" /> : <Eye size={13} />} Enviarme una prueba
                </button>
                <button type="button" onClick={confirmarEnvio} className="btn-success" disabled={enviando || enviandoPrueba}>
                  {enviando ? <Loader2 size={13} className="spin-icon" /> : <Send size={13} />}
                  {enviando ? ' Enviando...' : ` Enviar a ${suscriptores ?? 0} suscriptor(es)`}
                </button>
                <button type="button" onClick={() => setModo('lista')} className="btn-secondary" disabled={enviando}>
                  <ArrowLeft size={13} /> Volver
                </button>
              </div>
            </div>

            <div className="modal-scroll-body">
              <div className="form-group">
                <label htmlFor="boletin-asunto">Asunto:</label>
                <input
                  id="boletin-asunto"
                  type="text"
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  maxLength={200}
                  placeholder="Ej: Novedades de octubre en PsyTest"
                  disabled={enviando}
                />
              </div>

              <div className="boletin-barra-modo">
                <div className="boletin-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={vista === 'visual'}
                    className={vista === 'visual' ? 'activo' : ''}
                    onClick={() => setVista('visual')}
                  >
                    <Eye size={14} /> Editor visual
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={vista === 'codigo'}
                    className={vista === 'codigo' ? 'activo' : ''}
                    onClick={() => setVista('codigo')}
                  >
                    <Code size={14} /> HTML / CSS
                  </button>
                </div>
                <button type="button" className="boletin-link" onClick={empezarDeCero}>
                  <RotateCcw size={13} /> Empezar de cero
                </button>
              </div>

              <p className="boletin-nota">
                El encabezado con el logo y el aviso para darse de baja se agregan siempre a cada correo; aquí editas solo el contenido.
              </p>

              {vista === 'visual' ? (
                <>
                  <div className="boletin-toolbar" role="toolbar" aria-label="Formato del texto">
                    {BOTONES.map((grupo, i) => (
                      <div className="boletin-toolbar-grupo" key={i}>
                        {grupo.map(({ icono: Icono, titulo, accion }) => (
                          <button
                            key={titulo}
                            type="button"
                            title={titulo}
                            aria-label={titulo}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={accion}
                          >
                            <Icono size={15} />
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="boletin-marco">{iframe}</div>
                </>
              ) : (
                <div className="boletin-codigo">
                  <div className="boletin-codigo-editores">
                    <label htmlFor="boletin-html">HTML del contenido</label>
                    <textarea
                      id="boletin-html"
                      value={contenido}
                      onChange={(e) => setContenido(e.target.value)}
                      spellCheck={false}
                      rows={16}
                    />
                    <label htmlFor="boletin-css">CSS adicional</label>
                    <textarea
                      id="boletin-css"
                      value={css}
                      onChange={(e) => setCss(e.target.value)}
                      spellCheck={false}
                      rows={8}
                      placeholder={'h2 { color: #048377; }\n.content { padding: 40px; }'}
                    />
                    <small className="form-hint">
                      Clases disponibles: <code>.button</code>, <code>.info-box</code>, <code>.warning-box</code>, <code>.metric</code>.
                      Algunos clientes de correo ignoran ciertos estilos; usa estilos simples y envíate una prueba.
                    </small>
                  </div>
                  <div className="boletin-marco">{iframe}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoletinManager;
